import React from 'react';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  DEFAULT_LABOR_COST_SHARE_SETTINGS,
  formatLaborCostShareFootnote,
  laborCostShareAmounts,
  laborCostShareSettingsFromUnknown,
  type LaborCostShareSettings,
} from '../../leads/utils/offerLaborCostShare';
import { formatEuro, OFFER_TOTALS_COLUMN_WIDTH_PT } from '../../leads/utils/offerDocumentUtils';
import { OFFER_PDF_FONT_STYLE } from '../../leads/components/HtmlLayoutBands';

const btnOutline = SETTINGS_BTN_OUTLINE;
const btnPrimary = SETTINGS_BTN_PRIMARY;
const groupTitle = SETTINGS_FIELD_LABEL;

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30';

const PREVIEW_NOTE = formatLaborCostShareFootnote(laborCostShareAmounts(199.41, 19));
const PREVIEW_NET = formatEuro(731.09);
const PREVIEW_VAT = formatEuro(138.91);
const PREVIEW_GROSS = formatEuro(870);

type FormState = LaborCostShareSettings;

function FormCheckbox({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label htmlFor={id} className={`flex items-center gap-2.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
      <input
        id={id}
        type="checkbox"
        className={CHECKBOX_CLASS}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-app-text">{label}</span>
    </label>
  );
}

/** showOffers / showInvoices: Einstellungen nur für gebuchte Module (Angebote, Rechnungen). */
export function LaborCostShareSettingsSection({
  showOffers = true,
  showInvoices = true,
}: { showOffers?: boolean; showInvoices?: boolean } = {}) {
  const [form, setForm] = React.useState<FormState>(DEFAULT_LABOR_COST_SHARE_SETTINGS);
  const [editSnapshot, setEditSnapshot] = React.useState<FormState | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const editing = editSnapshot != null;

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setForm(laborCostShareSettingsFromUnknown(data));
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const beginEdit = () => {
    setEditSnapshot({ ...form });
    setError(null);
    setSaved(false);
  };

  const cancelEdit = () => {
    if (editSnapshot) setForm(editSnapshot);
    setEditSnapshot(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Speichern fehlgeschlagen.');
      }
      const data = await res.json();
      setForm(laborCostShareSettingsFromUnknown(data));
      setEditSnapshot(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editing || saving || !loaded;
  const dirty = editing && editSnapshot != null && !settingsValuesEqual(form, editSnapshot);

  return (
    <SettingsCard
      title="Ausweisung des Lohnkostenanteils"
      description={
        showInvoices && showOffers
          ? 'Du kannst den Hinweis zu den Lohnkosten in allen Angeboten und Rechnungen ein- oder ausblenden. Für Rechnungen an Privatkunden ist die Ausweisung gesetzlich vorgeschrieben. Die Einstellung kann für einzelne Angebote oder Rechnungen individuell überschrieben werden.'
          : showInvoices
            ? 'Du kannst den Hinweis zu den Lohnkosten in allen Rechnungen ein- oder ausblenden. Für Rechnungen an Privatkunden ist die Ausweisung gesetzlich vorgeschrieben. Die Einstellung kann für einzelne Rechnungen individuell überschrieben werden.'
            : 'Du kannst den Hinweis zu den Lohnkosten in allen Angeboten ein- oder ausblenden. Die Einstellung kann für einzelne Angebote individuell überschrieben werden.'
      }
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button type="button" className={btnOutline} onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button type="button" className={btnPrimary} onClick={() => void save()} disabled={saving || !dirty}>
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button type="button" className={btnOutline} onClick={beginEdit} disabled={!loaded}>
              Bearbeiten
            </button>
          )}
        </>
      }
    >
      {error || loadError ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error || loadError}</div>
      ) : null}

        <div className="grid gap-6 sm:grid-cols-2">
          {showOffers ? (
          <div>
            <p className={`${groupTitle} mb-3`}>Lohnkostenanteil in Angeboten ausweisen</p>
            <div className="space-y-2.5">
              <FormCheckbox
                id="labor-share-offer-private"
                label="Für Privatkunden"
                checked={form.labor_cost_share_offer_private}
                disabled={disabled}
                onChange={(v) => setForm((f) => ({ ...f, labor_cost_share_offer_private: v }))}
              />
              <FormCheckbox
                id="labor-share-offer-business"
                label="Für Geschäftskunden"
                checked={form.labor_cost_share_offer_business}
                disabled={disabled}
                onChange={(v) => setForm((f) => ({ ...f, labor_cost_share_offer_business: v }))}
              />
            </div>
          </div>
          ) : null}
          {showInvoices ? (
          <div>
            <p className={`${groupTitle} mb-3`}>Lohnkostenanteil in Rechnungen ausweisen</p>
            <div className="space-y-2.5">
              <FormCheckbox
                id="labor-share-invoice-private"
                label="Für Privatkunden"
                checked={form.labor_cost_share_invoice_private}
                disabled={disabled}
                onChange={(v) => setForm((f) => ({ ...f, labor_cost_share_invoice_private: v }))}
              />
              <FormCheckbox
                id="labor-share-invoice-business"
                label="Für Geschäftskunden"
                checked={form.labor_cost_share_invoice_business}
                disabled={disabled}
                onChange={(v) => setForm((f) => ({ ...f, labor_cost_share_invoice_business: v }))}
              />
            </div>
          </div>
          ) : null}
        </div>

        <div className="mt-6 max-w-2xl border-t border-app-border pt-5">
          <p className={`${groupTitle} mb-2.5`}>Vorschau im Kundendokument</p>
          <div className="rounded-md border border-app-border bg-app-surface px-5 py-4">
            <div
              className="ml-auto space-y-1 text-[9.5pt] text-app-text"
              style={{ width: `${OFFER_TOTALS_COLUMN_WIDTH_PT}pt` }}
            >
              <div className="flex justify-between gap-4 font-semibold">
                <span>Nettobetrag</span>
                <span className="tabular-nums">{PREVIEW_NET}</span>
              </div>
              <div className="flex justify-between gap-4 text-app-text-subtle">
                <span>zzgl. 19 % USt.</span>
                <span className="tabular-nums">{PREVIEW_VAT}</span>
              </div>
              <div className="mt-1.5 flex justify-between gap-4 border-t-2 border-slate-900 pt-2 font-semibold">
                <span>Bruttobetrag</span>
                <span className="tabular-nums">{PREVIEW_GROSS}</span>
              </div>
              <p
                className="pt-2 text-[8pt] font-normal leading-[1.4] text-app-text-subtle"
                style={OFFER_PDF_FONT_STYLE}
              >
                {PREVIEW_NOTE}
              </p>
            </div>
          </div>
        </div>
    </SettingsCard>
  );
}
